import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Apicall from "../../libs/Apicall";
import { couleurs } from "../../theme/tokens";

/**
 * Applications connectées (phase 5, docs/plan-api-sites-municipaux.md).
 *
 * L'écran de consentement de `compte.liencitoyen.com` promet au citoyen
 * qu'il pourra retirer un accès à tout moment : c'est ici qu'il le fait.
 * La section ne s'affiche que s'il a réellement autorisé un site — inutile
 * d'exposer une notion d'« applications » à qui n'en a jamais connecté.
 */

interface connectedAppInterface {
    client_id: string;
    name: string;
    vendor: string | null;
    city_name: string | null;
    scopes: string[];
    authorized_at: string | null;
}

export default function ConnectedApps() {
    const [apps, setApps] = useState<connectedAppInterface[]>([]);
    const [error, setError] = useState("");
    const [aRetirer, setARetirer] = useState<connectedAppInterface | null>(null);

    const charger = useCallback(() => {
        new Apicall().call("POST", "me/connected-apps").then((response) => {
            if (response.status === "OK") {
                // Voir SuperAdminApiClients : `Apicall` déballe déjà `data`.
                setApps(response.data ?? []);
            } else {
                setError(String(response.error));
            }
        });
    }, []);

    useEffect(() => {
        charger();
    }, [charger]);

    const retirer = (app: connectedAppInterface) => {
        new Apicall()
            .call("POST", "me/connected-apps/revoke", { client_id: app.client_id })
            .then((response) => {
                if (response.status === "OK") {
                    charger();
                } else {
                    setError(String(response.error));
                }
            });
        setARetirer(null);
    };

    if (apps.length === 0) {
        return null;
    }

    const dateLisible = (iso: string | null) =>
        iso ? new Date(iso).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" }) : null;

    return (
        <Box sx={{ mt: "60px" }}>
            <Typography
                variant="h5"
                sx={{ color: couleurs.BleuClair, fontWeight: 700, mb: 1 }}
            >
                Sites connectés à votre compte
            </Typography>
            <Typography variant="body2" sx={{ color: couleurs.GrisFonce, mb: 3 }}>
                Ces sites peuvent agir en votre nom dans Lien Citoyen. Retirer un accès
                l'interrompt immédiatement ; vous pourrez toujours l'autoriser de nouveau.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {apps.map((app) => (
                <Paper key={app.client_id} sx={{ p: 3, mb: 2, borderRadius: "12px" }}>
                    <Typography sx={{ fontWeight: 700, color: couleurs.BleuMarin }}>
                        {app.name}
                    </Typography>
                    {(app.vendor || app.city_name) && (
                        <Typography variant="caption" sx={{ color: couleurs.GrisFonce }}>
                            {[app.city_name, app.vendor].filter(Boolean).join(" · ")}
                        </Typography>
                    )}

                    <Box component="ul" sx={{ pl: 2.5, my: 1.5 }}>
                        {app.scopes.map((scope) => (
                            <Typography component="li" variant="body2" key={scope}>
                                {scope}
                            </Typography>
                        ))}
                    </Box>

                    {app.authorized_at && (
                        <Typography variant="caption" sx={{ color: couleurs.GrisFonce }}>
                            Autorisé le {dateLisible(app.authorized_at)}
                        </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ borderRadius: 20 }}
                            onClick={() => setARetirer(app)}
                        >
                            Retirer l'accès
                        </Button>
                    </Box>
                </Paper>
            ))}

            <Dialog open={aRetirer !== null} onClose={() => setARetirer(null)}>
                <DialogTitle>Retirer l'accès de {aRetirer?.name} ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Ce site ne pourra plus accéder à votre compte Lien Citoyen. Vos demandes
                        et vos réponses déjà envoyées sont conservées.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setARetirer(null)}>Annuler</Button>
                    <Button
                        color="error"
                        variant="contained"
                        sx={{ borderRadius: 20 }}
                        onClick={() => aRetirer && retirer(aRetirer)}
                    >
                        Retirer l'accès
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
